/**
 * SoleadLab 2.0 — IV Block Mapper
 *
 * Visual mapper for files where each measurement occupies a rectangular BLOCK
 * of rows (e.g. HT Italia IV600: rows 1-15 = measurement 1, rows 17-31 = measurement 2).
 *
 * Workflow:
 *  1. Auto-detect (or manually set) block size + start row.
 *  2. One block is shown as a full grid — all rows × all columns.
 *  3. Click a metric chip → click a cell in the grid → that cell is assigned.
 *  4. Repeat for each metric you want to extract.
 */

import { useState, useMemo } from 'react';
import { IV_METRIC_FIELDS, type BlockCellRef, type BlockMapping, type BlockDetectionResult } from './ivParser';

// ─── Colour palette (shared with IVColumnMapper) ──────────────────────────────

const FIELD_COLOR: Record<string, string> = {
  label:       '#6366f1',
  voltage_oc:  '#3b82f6',
  current_sc:  '#22c55e',
  voltage_mpp: '#8b5cf6',
  current_mpp: '#10b981',
  power_mpp:   '#f97316',
  fill_factor: '#d97706',
  irradiance:  '#ef4444',
  module_temp: '#ec4899',
};

const FIELD_BG: Record<string, string> = {
  label:       '#eef2ff',
  voltage_oc:  '#eff6ff',
  current_sc:  '#f0fdf4',
  voltage_mpp: '#f5f3ff',
  current_mpp: '#ecfdf5',
  power_mpp:   '#fff7ed',
  fill_factor: '#fffbeb',
  irradiance:  '#fff1f2',
  module_temp: '#fdf4ff',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert 0-based column index to Excel-style letter (0=A, 1=B, 25=Z, 26=AA) */
function colLetter(col: number): string {
  let result = '';
  let n = col;
  do {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
}

function cellKey(row: number, col: number) { return `${row}:${col}`; }

// ─── Props ────────────────────────────────────────────────────────────────────

interface IVBlockMapperProps {
  rawRows: unknown[][];
  blockSize: number;
  blockFirstRow: number;
  blockCellMapping: BlockMapping;
  blockDetected: BlockDetectionResult | null;
  errors: string[];
  onBlockSizeChange: (n: number) => void;
  onBlockFirstRowChange: (n: number) => void;
  onBlockCellMappingChange: (m: BlockMapping) => void;
  onExitBlockMode: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function IVBlockMapper({
  rawRows,
  blockSize,
  blockFirstRow,
  blockCellMapping,
  blockDetected,
  errors,
  onBlockSizeChange,
  onBlockFirstRowChange,
  onBlockCellMappingChange,
  onExitBlockMode,
}: IVBlockMapperProps) {
  const [activeField, setActiveField] = useState<string | null>(null);

  // ── Slice the first block for the preview grid ───────────────────────────────
  const blockRows = useMemo(() => {
    const end = Math.min(blockFirstRow + blockSize, rawRows.length);
    return rawRows.slice(blockFirstRow, end);
  }, [rawRows, blockFirstRow, blockSize]);

  // Column count across the block
  const numCols = useMemo(
    () => Math.max(...blockRows.map((r) => (Array.isArray(r) ? r.length : 0)), 0),
    [blockRows]
  );

  // Reverse lookup: "row:col" → metricKey
  const cellToMetric = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [key, ref] of Object.entries(blockCellMapping)) {
      if (ref) out[cellKey(ref.row, ref.col)] = key;
    }
    return out;
  }, [blockCellMapping]);

  const mappedCount = Object.values(blockCellMapping).filter(Boolean).length;
  const activeFieldDef = IV_METRIC_FIELDS.find((f) => f.key === activeField);

  // ── Estimated measurement count ──────────────────────────────────────────────
  const estimatedBlocks = blockSize > 0
    ? Math.floor((rawRows.length - blockFirstRow) / blockSize)
    : 0;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleChipClick = (key: string) => {
    setActiveField((prev) => (prev === key ? null : key));
  };

  const handleClearChip = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = { ...blockCellMapping };
    delete next[key];
    onBlockCellMappingChange(next);
    if (activeField === key) setActiveField(null);
  };

  const handleCellClick = (rowInBlock: number, col: number) => {
    if (!activeField) return;

    const next = { ...blockCellMapping };

    // If this cell is already assigned to another metric, clear that
    const prev = cellToMetric[cellKey(rowInBlock, col)];
    if (prev && prev !== activeField) delete next[prev];

    next[activeField] = { row: rowInBlock, col };
    onBlockCellMappingChange(next);
    setActiveField(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="iv-mapper iv-block-mapper">

      {/* ── Mode exit ──────────────────────────────────────────────── */}
      <div className="iv-block-mapper__back">
        <button className="iv-btn iv-btn--sm" onClick={onExitBlockMode}>
          ← Standard / Transposed
        </button>
        <span className="iv-block-mapper__mode-label">Block Template Mode</span>
      </div>

      {/* ── Detection banner ───────────────────────────────────────── */}
      {blockDetected && blockDetected.method !== 'none' && (
        <div className="iv-note iv-note--info" style={{ fontSize: 'var(--sl-text-xs)' }}>
          Auto-detected <strong>{blockDetected.totalBlocks} measurement blocks</strong> of{' '}
          <strong>{blockDetected.blockSize} rows</strong> each
          {blockDetected.method === 'empty-rows' ? ' (separated by empty rows)' : ' (repeating header row)'}.
          Adjust below if needed.
        </div>
      )}
      {blockDetected?.method === 'none' && (
        <div className="iv-note" style={{ fontSize: 'var(--sl-text-xs)' }}>
          Could not auto-detect block structure. Set the block size and start row manually.
        </div>
      )}

      {/* ── Block configuration ────────────────────────────────────── */}
      <div className="iv-block-config">
        <div className="iv-block-config__field">
          <label>Rows per measurement block</label>
          <input
            type="number"
            className="iv-input iv-block-config__input"
            min={1}
            max={500}
            value={blockSize}
            onChange={(e) => onBlockSizeChange(Math.max(1, parseInt(e.target.value) || 1))}
          />
        </div>
        <div className="iv-block-config__field">
          <label>First block starts at row</label>
          <input
            type="number"
            className="iv-input iv-block-config__input"
            min={1}
            value={blockFirstRow + 1}
            onChange={(e) => onBlockFirstRowChange(Math.max(0, parseInt(e.target.value) - 1 || 0))}
          />
        </div>
        <div className="iv-block-config__info">
          {estimatedBlocks > 0
            ? <><strong>{estimatedBlocks}</strong> measurements will be extracted</>
            : 'Set block size to continue'
          }
        </div>
      </div>

      {/* ── Metric palette ─────────────────────────────────────────── */}
      <div className="iv-mapper-palette">
        <div className="iv-mapper-palette-label">
          {activeField
            ? <>Click a cell in the block below to assign <strong>{activeFieldDef?.label}</strong>:</>
            : <>Tap a metric, then click the matching cell in the block:</>
          }
        </div>
        <div className="iv-mapper-palette-chips">
          {IV_METRIC_FIELDS.map((field) => {
            const ref = blockCellMapping[field.key] as BlockCellRef | undefined;
            const isAssigned = !!ref;
            const isActive = activeField === field.key;
            const color = FIELD_COLOR[field.key];
            const bg = FIELD_BG[field.key];

            return (
              <button
                key={field.key}
                className={`iv-metric-tag${isActive ? ' iv-metric-tag--active' : ''}${isAssigned ? ' iv-metric-tag--assigned' : ''}`}
                style={isAssigned || isActive ? { '--tag-color': color, '--tag-bg': isAssigned ? bg : 'transparent' } as React.CSSProperties : {}}
                onClick={() => handleChipClick(field.key)}
                title={isAssigned ? `${field.label} → Row ${ref!.row + 1}, Col ${colLetter(ref!.col)}` : field.label}
              >
                <span className="iv-metric-tag__label">
                  {field.label}{field.required && <sup>*</sup>}
                </span>
                {isAssigned && ref && (
                  <>
                    <span className="iv-metric-tag__col">
                      → R{ref.row + 1}{colLetter(ref.col)}
                    </span>
                    <span className="iv-metric-tag__clear" onClick={(e) => handleClearChip(field.key, e)}>×</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Errors ─────────────────────────────────────────────────── */}
      {errors.length > 0 && (
        <div className="iv-note iv-note--error" style={{ margin: '4px 0' }}>
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {/* ── Block grid ─────────────────────────────────────────────── */}
      {blockRows.length > 0 && numCols > 0 ? (
        <div className="iv-mapper-grid-wrap">
          <table className="iv-mapper-grid iv-block-grid">
            {/* Column header: A B C D E F G … */}
            <thead>
              <tr>
                <th className="iv-block-grid__row-num" />
                {Array.from({ length: numCols }, (_, c) => (
                  <th key={c} className="iv-block-grid__col-letter">
                    {colLetter(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blockRows.map((rawRow, rowIdx) => {
                const row = Array.isArray(rawRow) ? rawRow : [];
                return (
                  <tr key={rowIdx}>
                    {/* Row number (1-indexed within block) */}
                    <td className="iv-block-grid__row-num">
                      {rowIdx + 1}
                    </td>
                    {Array.from({ length: numCols }, (_, colIdx) => {
                      const ck = cellKey(rowIdx, colIdx);
                      const metricKey = cellToMetric[ck];
                      const field = metricKey ? IV_METRIC_FIELDS.find((f) => f.key === metricKey) : null;
                      const color = metricKey ? FIELD_COLOR[metricKey] : undefined;
                      const bg = metricKey ? FIELD_BG[metricKey] : undefined;
                      const val = row[colIdx];
                      const displayVal = val != null ? String(val).slice(0, 18) : '';
                      const isClickable = !!activeField;
                      const isEmpty = displayVal === '';

                      return (
                        <td
                          key={colIdx}
                          className={`iv-block-cell${isClickable ? ' iv-block-cell--clickable' : ''}${metricKey ? ' iv-block-cell--assigned' : ''}${isEmpty ? ' iv-block-cell--empty' : ''}`}
                          style={metricKey ? {
                            background: bg,
                            outline: `2px solid ${color}`,
                            outlineOffset: '-2px',
                          } : {}}
                          onClick={() => handleCellClick(rowIdx, colIdx)}
                          title={val != null ? String(val) : ''}
                        >
                          {field && (
                            <span
                              className="iv-block-cell__chip"
                              style={{ background: color, color: '#fff' }}
                            >
                              {field.label}
                            </span>
                          )}
                          <span className="iv-block-cell__val">{displayVal}</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="iv-note">No data in selected block range. Adjust the row settings above.</div>
      )}

      {/* ── Status bar ─────────────────────────────────────────────── */}
      <div className="iv-mapper-status">
        <span className="iv-mapper-status__count">{mappedCount} of {IV_METRIC_FIELDS.length} metrics assigned</span>
        <span className="iv-mapper-status__dots">
          {IV_METRIC_FIELDS.map((f) => (
            <span
              key={f.key}
              className="iv-mapper-status__dot"
              style={blockCellMapping[f.key] ? { background: FIELD_COLOR[f.key] } : {}}
              title={`${f.label}${blockCellMapping[f.key] ? '' : ' (not assigned)'}`}
            />
          ))}
        </span>
      </div>

    </div>
  );
}
